import { useQuery } from "@tanstack/react-query";
import { reportService } from "../../services/reports";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import { Users, Target, TrendingUp, XCircle } from "lucide-react";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4"];

const MetricCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const LeadReport = ({ filters }) => {
  const { data } = useQuery({
    queryKey: ["lead-report", filters],
    queryFn: () => reportService.getLeadReport(filters),
  });

  if (!data) return <div className="text-center py-8 text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Users} label="Total Leads" value={data.totalLeads || 0} color="bg-blue-500" />
        <MetricCard icon={Target} label="Converted" value={data.convertedLeads || 0} color="bg-emerald-500" />
        <MetricCard icon={TrendingUp} label="Conversion Rate" value={`${data.conversionRate || 0}%`} color="bg-purple-500" />
        <MetricCard icon={XCircle} label="Lost" value={data.lostLeads || 0} color="bg-red-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Leads by Source</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.leadsBySource}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Leads by Status</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.leadsByStatus} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="count" nameKey="_id">
                {(data.leadsByStatus || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend formatter={(value) => value.replace("_", " ")} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {data.monthlyTrend?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Monthly Lead Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" name="Total" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="converted" name="Converted" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.leadsByScore?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Lead Score Distribution</h3>
          <div className="grid grid-cols-3 gap-4">
            {data.leadsByScore.map((item) => (
              <div key={item._id} className={`rounded-xl p-4 text-center ${
                item._id === "hot" ? "bg-red-50" : item._id === "warm" ? "bg-amber-50" : "bg-blue-50"
              }`}>
                <p className="text-2xl font-bold text-gray-900">{item.count}</p>
                <p className={`text-sm font-medium capitalize ${
                  item._id === "hot" ? "text-red-600" : item._id === "warm" ? "text-amber-600" : "text-blue-600"
                }`}>{item._id || "Unscored"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadReport;
